import { useState, useCallback, useRef } from "react";
import Resumable from "resumablejs";
import { connect, ConnectedProps } from "react-redux";
import {
  resumableFiles,
  createUploadEngine,
  addFileToUploadEngine,
  isFileReadable,
  safeCancel,
  FILE_CHANGED_MESSAGE,
} from "../Upload/index";
import { enqueueBulkUpload } from "./bulkUploadQueue";
import { callRegisterMediaOnCms } from "@/store/upload/upload.action";
import {
  updateFileProgress,
  setUploadedSuccessfully,
  setUploadingError,
  decreaseCounter,
} from "@/store/upload/upload.slice";
import { showUploadProgress, setSoftRefresh } from "@/store/app/app.slice";
import { toast } from "..";
import {
  getMediaDimensions,
  computeAspectRatioAndOrientation,
} from "@/utils/createOptionsUtils";
import { Files, UploadCloud, X } from "lucide-react";
import { ALLOWED_FILE_EXTENSION } from "@/utils/constants/uiConstants";

export interface BulkMediaItem {
  id: string;
  name: string;
}

// Track all active bulk Resumable instances so the visibility handler can
// re-kick them when the tab comes back into view (browsers throttle setTimeout
// to ≥1 s in hidden tabs, which stalls Resumable's chunk scheduling loop). Not
// needed for the WorkerUploadEngine path — its chunk pump runs in a Web Worker,
// which isn't throttled the same way — so only main-thread Resumable instances
// are tracked here (isUploading() isn't implemented on the worker bridge).
const activeBulkResumables = new Set<Resumable>();

// Consecutive chunk-retry threshold before re-checking file readability — see
// the fileRetry handler below. Mirrors the <Uploader> drop-zone guard so a
// file that changed on disk (renamed/moved/truncated) fails fast instead of
// grinding through all maxChunkRetries (50) attempts per chunk, which would
// otherwise hold its bulk-upload queue slot for ~100s before finally erroring.
const RETRY_THRESHOLD = 5;

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      activeBulkResumables.forEach((r) => {
        if (r.isUploading()) r.upload();
      });
    }
  });
}

interface BulkUploadFormOwnProps {
  closeModal: () => void;
  onUploadsStarted: (items: BulkMediaItem[]) => void;
}

type Props = ConnectedProps<typeof connector> & BulkUploadFormOwnProps;

const BulkUploadFormBase = ({
  closeModal,
  onUploadsStarted,
  callRegisterMediaOnCms,
  updateFileProgress,
  setUploadedSuccessfully,
  setUploadingError,
  decreaseCounter,
  showUploadProgress,
  setSoftRefresh,
}: Props) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const BULK_ALLOWED_AUDIO_EXTS = ALLOWED_FILE_EXTENSION.AUDIO;
  const BULK_ALLOWED_VIDEO_EXTS = ALLOWED_FILE_EXTENSION.VIDEO;
  const BULK_ALLOWED_EXTS = [
    ...BULK_ALLOWED_AUDIO_EXTS,
    ...BULK_ALLOWED_VIDEO_EXTS,
  ];
  const allowedExts = BULK_ALLOWED_EXTS.map((e) => `.${e}`).join(",");
  const BULK_MAX_FILES =
    Number(import.meta.env.VITE_BULK_UPLOAD_MAX_FILES) || 5;

  const getAssetType = (file: File) =>
    BULK_ALLOWED_AUDIO_EXTS.some((ext) =>
      file.name.toLowerCase().endsWith(`.${ext}`)
    )
      ? "audio"
      : "video";

  const isAllowedFile = (file: File) =>
    BULK_ALLOWED_EXTS.some((ext) =>
      file.name.toLowerCase().endsWith(`.${ext}`)
    );

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const arr = Array.from(files);
      const rejected: string[] = [];
      const valid = arr.filter((f) => {
        if (isAllowedFile(f)) return true;
        rejected.push(f.name);
        return false;
      });
      if (rejected.length) {
        toast.fail(
          `Only ${BULK_ALLOWED_EXTS.join(", ").toUpperCase()} files are allowed. Skipped: ${rejected.join(", ")}`
        );
      }
      setSelectedFiles((prev) => {
        const existingKeys = new Set(prev.map((f) => `${f.name}-${f.size}`));
        const fresh = valid.filter(
          (f) => !existingKeys.has(`${f.name}-${f.size}`)
        );
        const remainingSlots = BULK_MAX_FILES - prev.length;
        if (remainingSlots <= 0) {
          if (fresh.length) {
            toast.fail(`Only ${BULK_MAX_FILES} media allowed at a time.`);
          }
          return prev;
        }
        const accepted = fresh.slice(0, remainingSlots);
        if (fresh.length > accepted.length) {
          toast.fail(`Only ${BULK_MAX_FILES} media allowed at a time.`);
        }
        return [...prev, ...accepted];
      });
    },
    [BULK_MAX_FILES]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) addFiles(e.target.files);
      e.target.value = "";
    },
    [addFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const removeFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const startUpload = useCallback(() => {
    if (!selectedFiles.length || isUploading) return;
    setIsUploading(true);
    showUploadProgress();

    const registeredItems: BulkMediaItem[] = [];
    let pending = selectedFiles.length;

    const checkDone = () => {
      pending -= 1;
      if (pending === 0 && registeredItems.length > 0) {
        onUploadsStarted(registeredItems);
        closeModal();
      }
    };

    selectedFiles.forEach((nativeFile) => {
      getMediaDimensions(nativeFile).then(({ width, height }) => {
        const { aspect_ratio, orientation } = computeAspectRatioAndOrientation(
          width,
          height
        );

        const identifier = `${nativeFile.size}-${nativeFile.name.replace(/[.\s]/g, "")}`;

        const fileProxy = {
          file: nativeFile,
          name: nativeFile.name,
          fileName: nativeFile.name,
          size: nativeFile.size,
          uniqueIdentifier: identifier,
        };

        let assetType = getAssetType(nativeFile);
        let isShorts = false;
        if (orientation === 2) {
          assetType = "shorts";
          isShorts = true;
        }

        const params = {
          file: fileProxy,
          size: nativeFile.size,
          name: "media",
          type: assetType,
          asset_type: assetType,
          id: assetType,
          isShorts,
          aspect_ratio,
          orientation,
          data: { title: nativeFile.name, meta_config: null },
        };

        callRegisterMediaOnCms(params, (response: any) => {
          if (!response || response.status !== 200) {
            toast.fail(
              response?.error || `Failed to register ${nativeFile.name}`
            );
            checkDone();
            return;
          }

          const mediaId = response.data.id;
          const token = response.data.jwt;

          registeredItems.push({ id: mediaId, name: nativeFile.name });

          const r = createUploadEngine({
            query: {
              method: "asset.resumable",
              entity: mediaId,
              key: "media",
            },
            headers: { token },
            chunkSize: 2 * 1024 * 1024,
          });

          // Re-kick tracking is only meaningful for the main-thread engine;
          // see activeBulkResumables' comment above.
          if (r instanceof Resumable) activeBulkResumables.add(r);

          let releaseSlot: (() => void) | null = null;
          const releaseOnce = () => {
            releaseSlot?.();
            releaseSlot = null;
          };

          let retryCount = 0;

          r.on("fileAdded", (rf) => {
            resumableFiles[rf.uniqueIdentifier] = rf;

            r.on("fileProgress", (f) => {
              updateFileProgress({
                identifier,
                progress: (f.progress(false) * 100) | 0,
              });
            });

            r.on("fileSuccess", () => {
              if (r instanceof Resumable) activeBulkResumables.delete(r);
              decreaseCounter();
              setUploadedSuccessfully({ identifier, flag: true });
              setSoftRefresh(true);
              releaseOnce();
            });

            r.on("fileError", () => {
              if (r instanceof Resumable) activeBulkResumables.delete(r);
              decreaseCounter();
              setUploadingError({
                uniqueIdentifier: identifier,
                error: "Error while uploading",
              });
              releaseOnce();
            });

            // If the file changes on disk mid-upload (renamed/moved/truncated),
            // chunk reads start failing and the engine treats it as a transient
            // network error, retrying up to maxChunkRetries (50) per chunk —
            // pointless if the file won't un-change, and it holds this file's
            // queue slot the whole time, stalling the rest of the batch behind
            // it. After a few consecutive retries, re-verify readability and
            // abort with a clear message instead of grinding through all 50.
            r.on("fileRetry", async (rf: any) => {
              retryCount += 1;
              if (retryCount < RETRY_THRESHOLD) return;

              const readable = await isFileReadable(rf.file);
              if (!readable) {
                // cancel(), not abort(): abort only pauses in-flight chunks and
                // leaves the file queued, so the retry loop picks it right back up.
                safeCancel(rf);
                if (r instanceof Resumable) activeBulkResumables.delete(r);
                decreaseCounter();
                setUploadingError({
                  uniqueIdentifier: identifier,
                  error: FILE_CHANGED_MESSAGE,
                });
                releaseOnce();
              }
            });

            // Registration (media.register) already happened for every file up
            // front; only the actual chunk transfer is throttled, queued FIFO
            // across this and any other in-flight bulk-upload batch.
            const { release } = enqueueBulkUpload(() => r.upload());
            releaseSlot = release;
          });

          addFileToUploadEngine(r, nativeFile, identifier);
          checkDone();
        });
      });
    });
  }, [
    selectedFiles,
    isUploading,
    showUploadProgress,
    callRegisterMediaOnCms,
    updateFileProgress,
    decreaseCounter,
    setUploadedSuccessfully,
    setUploadingError,
    setSoftRefresh,
    onUploadsStarted,
    closeModal,
  ]);

  return (
    <div style={{ padding: "16px 0" }}>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={allowedExts}
        style={{ display: "none" }}
        onChange={handleInputChange}
      />

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${isDragging ? "#0d6efd" : "#ced4da"}`,
          borderRadius: 8,
          padding: "32px 24px",
          textAlign: "center",
          background: isDragging ? "#e8f0fe" : "#f8f9fa",
          cursor: "pointer",
          transition: "border-color 0.15s ease, background 0.15s ease",
          userSelect: "none",
        }}
      >
        <UploadCloud
          size={36}
          strokeWidth={1.3}
          style={{ color: isDragging ? "#0d6efd" : "#6c757d", marginBottom: 8 }}
        />
        <div style={{ fontSize: 14, fontWeight: 500, color: "#343a40" }}>
          Click to browse or drag &amp; drop files here
        </div>
        <div style={{ fontSize: 12, color: "#6c757d", marginTop: 4 }}>
          Select multiple videos, audio or other media files
        </div>
        <div style={{ fontSize: 12, color: "#6c757d", marginTop: 2 }}>
          Only {BULK_MAX_FILES} media allowed at a time
        </div>
      </div>

      {selectedFiles.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 500, color: "#495057" }}>
              {selectedFiles.length} / {BULK_MAX_FILES} file
              {selectedFiles.length !== 1 ? "s" : ""} selected
            </span>
            <button
              onClick={() => setSelectedFiles([])}
              style={{
                fontSize: 12,
                color: "#dc3545",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
            >
              Clear all
            </button>
          </div>
          <div
            style={{
              maxHeight: 240,
              overflowY: "auto",
              border: "1px solid #dee2e6",
              borderRadius: 6,
            }}
          >
            {selectedFiles.map((f, i) => (
              <div
                key={`${f.name}-${f.size}-${i}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  borderBottom:
                    i < selectedFiles.length - 1 ? "1px solid #f1f3f5" : "none",
                  fontSize: 13,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <Files
                    size={14}
                    style={{ color: "#6c757d", flexShrink: 0 }}
                  />
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "#212529",
                    }}
                    title={f.name}
                  >
                    {f.name}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexShrink: 0,
                  }}
                >
                  <span style={{ color: "#868e96", fontSize: 12 }}>
                    {formatFileSize(f.size)}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(i);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      lineHeight: 0,
                      color: "#adb5bd",
                    }}
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 20,
          gap: 8,
        }}
      >
        <button
          onClick={closeModal}
          style={{
            padding: "6px 16px",
            fontSize: 13,
            borderRadius: 4,
            border: "1px solid #ced4da",
            background: "white",
            cursor: "pointer",
            color: "#495057",
          }}
        >
          Cancel
        </button>
        <button
          onClick={startUpload}
          disabled={selectedFiles.length === 0 || isUploading}
          style={{
            padding: "6px 20px",
            fontSize: 13,
            borderRadius: 4,
            border: "none",
            background:
              selectedFiles.length === 0 || isUploading ? "#adb5bd" : "#0d6efd",
            color: "white",
            cursor:
              selectedFiles.length === 0 || isUploading
                ? "not-allowed"
                : "pointer",
            fontWeight: 500,
          }}
        >
          {isUploading
            ? "Registering..."
            : `Upload ${selectedFiles.length > 0 ? selectedFiles.length + " " : ""}file${selectedFiles.length !== 1 ? "s" : ""}`}
        </button>
      </div>
    </div>
  );
};

const mapDispatch = {
  callRegisterMediaOnCms,
  updateFileProgress,
  setUploadedSuccessfully,
  setUploadingError,
  decreaseCounter,
  showUploadProgress,
  setSoftRefresh,
};

const connector = connect(null, mapDispatch);
export const BulkUploadForm = connector(BulkUploadFormBase);
